import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Html5Qrcode } from "html5-qrcode";
import { useAuth } from "../../context/AuthContext";

export default function ScanQRModal({ open, onClose }) {
  const { authFetch } = useAuth();
  const scannerRef = useRef(null);
  const startingRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [scannerReady, setScannerReady] = useState(false);
  const [member, setMember] = useState(null);
  const [membership, setMembership] = useState(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [extractedToken, setExtractedToken] = useState("");

  const resetState = () => {
    setLoading(false);
    setScannerReady(false);
    setMember(null);
    setMembership(null);
    setError("");
    setSuccessMessage("");
    setExtractedToken("");
  };

  const extractToken = (decodedText) => {
    const raw = String(decodedText || "").trim();

    if (!raw) return "";

    try {
      const url = new URL(raw);
      const path = url.pathname.replace(/\/+$/, "");

      const memberMatch = path.match(/\/member\/([0-9a-fA-F-]+)$/i);
      if (memberMatch?.[1]) return memberMatch[1];

      const apiMatch = path.match(/\/api\/gym\/member\/qr\/([0-9a-fA-F-]+)$/i);
      if (apiMatch?.[1]) return apiMatch[1];
    } catch {}

    const memberMatch = raw.match(/\/member\/([0-9a-fA-F-]+)/i);
    if (memberMatch?.[1]) return memberMatch[1];

    const apiMatch = raw.match(/\/api\/gym\/member\/qr\/([0-9a-fA-F-]+)/i);
    if (apiMatch?.[1]) return apiMatch[1];

    const uuidMatch = raw.match(
      /\b[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\b/i
    );
    if (uuidMatch?.[0]) return uuidMatch[0];

    return "";
  };

  const safelyStopScanner = async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;

    try {
      const state = typeof scanner.getState === "function" ? scanner.getState() : null;
      if (state === 2 || state === 1) {
        await scanner.stop();
      }
    } catch {}

    try {
      await scanner.clear();
    } catch {}

    scannerRef.current = null;
    startingRef.current = false;
    setScannerReady(false);
  };

  const loadMember = async (token) => {
    const url = `/api/gym/member/qr/${token}/`;

    setLoading(true);
    setError("");
    setSuccessMessage("");
    setMember(null);
    setMembership(null);

    try {
      const response = await authFetch(url, {
        method: "GET",
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        setError(data?.error || "Usuario no registrado");
        return;
      }

      const nextMember =
        data?.user ||
        data?.member ||
        (data?.id && data?.role ? data : null);

      const nextMembership =
        data?.active_membership || data?.membership || null;

      if (!nextMember) {
        setError("Usuario no registrado");
        return;
      }

      setMember(nextMember);
      setMembership(nextMembership);

      if (!nextMembership) {
        setSuccessMessage("Usuario registrado sin membresía activa");
      }
    } catch {
      setError("No se pudo validar el QR");
    } finally {
      setLoading(false);
    }
  };

  const startScanner = async () => {
    if (startingRef.current) return;

    startingRef.current = true;
    setError("");
    setSuccessMessage("");
    setMember(null);
    setMembership(null);
    setScannerReady(false);
    setExtractedToken("");

    await new Promise((resolve) => setTimeout(resolve, 250));

    const element = document.getElementById("qr-reader");
    if (!element) {
      startingRef.current = false;
      return;
    }

    try {
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: 250,
          aspectRatio: 1,
        },
        async (decodedText) => {
          const rawValue = String(decodedText || "").trim();
          const token = extractToken(rawValue);

          setExtractedToken(token);

          await safelyStopScanner();

          if (!token) {
            setError("QR inválido o no pertenece al sistema");
            return;
          }

          await loadMember(token);
        },
        () => {}
      );

      setScannerReady(true);
    } catch {
      setError("No se pudo abrir la cámara");
    } finally {
      startingRef.current = false;
    }
  };

  const handleRetryScan = async () => {
    resetState();
    await safelyStopScanner();
    await startScanner();
  };

  const registerVisit = async () => {
    if (!membership?.id) return;

    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await authFetch(
        `/api/memberships/memberships/${membership.id}/visits/create/`,
        {
          method: "POST",
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        setError(data?.error || "No se pudo registrar la visita");
        return;
      }

      const nextMembership = data?.membership || null;

      if (nextMembership) {
        setMembership(nextMembership);
      } else {
        await loadMember(extractedToken);
      }

      setSuccessMessage(data?.message || "Visita registrada correctamente");
    } catch {
      setError("Error registrando la visita");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    resetState();
    startScanner();

    return () => {
      safelyStopScanner();
    };
  }, [open]);

  const fullName = member
    ? `${member.first_name || ""} ${member.last_name || ""}`.trim() || "Sin nombre"
    : "";

  const planName =
    membership?.plan_name || membership?.plan?.name || "Sin plan";

  const usageType =
    membership?.plan_usage_type || membership?.plan?.usage_type || "";

  const visitsRemaining =
    membership?.visits_remaining ?? membership?.remaining_visits ?? null;

  const visitsUsed =
    membership?.visits_used ?? 0;

  const visitsTotal =
    membership?.visits_total ?? 0;

  const getInitial = (item) => {
    const full =
      `${item?.first_name || ""} ${item?.last_name || ""}`.trim() ||
      item?.email ||
      "U";
    return full.charAt(0).toUpperCase();
  };

  const getPhoto = (item) => {
    return item?.profile?.profile_photo || item?.profile_photo || "";
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Escanear QR</DialogTitle>

      <DialogContent>
        {!member && !loading && (
          <Stack spacing={2}>
            <div id="qr-reader" style={{ width: "100%" }} />
            {!scannerReady && !error && (
              <Stack alignItems="center" spacing={1} sx={{ py: 1 }}>
                <CircularProgress size={24} />
                <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                  Iniciando cámara...
                </Typography>
              </Stack>
            )}
          </Stack>
        )}

        {loading && (
          <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
            <CircularProgress />
            <Typography>Validando...</Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {successMessage}
          </Alert>
        )}

        {member && (
          <Paper
            sx={{
              p: 2,
              mt: 2,
              borderRadius: "14px",
              border: "1px solid rgba(148,163,184,0.18)",
            }}
          >
            <Stack spacing={1.6}>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Avatar
                  src={getPhoto(member) || undefined}
                  alt={fullName}
                  imgProps={{ referrerPolicy: "no-referrer" }}
                  sx={{
                    width: 54,
                    height: 54,
                    flexShrink: 0,
                    fontSize: 18,
                    fontWeight: 700,
                    bgcolor: "rgba(59,130,246,0.12)",
                    color: "#1d4ed8",
                    border: "1px solid rgba(148,163,184,0.18)",
                  }}
                >
                  {getInitial(member)}
                </Avatar>

                <Stack spacing={0.2} sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a",
                      lineHeight: 1.2,
                    }}
                  >
                    {fullName}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "#64748b",
                      lineHeight: 1.3,
                      wordBreak: "break-word",
                    }}
                  >
                    {member.email || "Sin correo"}
                  </Typography>
                </Stack>
              </Stack>

              <Typography sx={{ fontSize: 14, color: "#475569" }}>
                Teléfono: {member.phone || "Sin teléfono"}
              </Typography>

              <Typography sx={{ fontSize: 14, color: "#475569" }}>
                Plan: {membership ? planName : "Sin membresía activa"}
              </Typography>

              <Typography sx={{ fontSize: 14, color: "#475569" }}>
                Estado: {membership?.status || "Sin membresía activa"}
              </Typography>

              <Typography sx={{ fontSize: 14, color: "#475569" }}>
                Inicio: {membership?.start_date || "—"}
              </Typography>

              <Typography sx={{ fontSize: 14, color: "#475569" }}>
                Fin: {membership?.end_date || "—"}
              </Typography>

              {membership && usageType === "visits" && (
                <>
                  <Typography sx={{ fontSize: 14, color: "#475569" }}>
                    Visitas totales: {visitsTotal}
                  </Typography>

                  <Typography sx={{ fontSize: 14, color: "#475569" }}>
                    Visitas usadas: {visitsUsed}
                  </Typography>

                  <Typography sx={{ fontSize: 14, color: "#475569" }}>
                    Visitas restantes: {visitsRemaining ?? 0}
                  </Typography>
                </>
              )}

              {membership && usageType === "calendar" && (
                <Typography sx={{ fontSize: 14, color: "#475569" }}>
                  Tipo: Calendario
                </Typography>
              )}

              {membership && usageType === "visits" && (visitsRemaining ?? 0) > 0 && (
                <Button
                  variant="contained"
                  onClick={registerVisit}
                  disabled={loading}
                  sx={{
                    mt: 1,
                    bgcolor: "#0f172a",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#111827",
                      boxShadow: "none",
                    },
                  }}
                >
                  Registrar visita
                </Button>
              )}

              {membership && usageType === "visits" && (visitsRemaining ?? 0) <= 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Esta membresía ya no tiene visitas disponibles
                </Alert>
              )}
            </Stack>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.2 }}>
        <Button
          variant="outlined"
          onClick={handleRetryScan}
          disabled={loading}
          sx={{
            height: 36,
            px: 1.4,
            borderRadius: "9px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 12.8,
            color: "#334155",
            borderColor: "rgba(51,65,85,0.18)",
            "&:hover": {
              borderColor: "rgba(51,65,85,0.28)",
              bgcolor: "rgba(148,163,184,0.08)",
            },
          }}
        >
          Reintentar
        </Button>

        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          sx={{
            height: 36,
            px: 1.4,
            borderRadius: "9px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 12.8,
            color: "#334155",
            borderColor: "rgba(51,65,85,0.18)",
            "&:hover": {
              borderColor: "rgba(51,65,85,0.28)",
              bgcolor: "rgba(148,163,184,0.08)",
            },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}